import * as React from "react"

import { cn } from "@/lib/utils"

/** Horizontal split with a keyboard-accessible separator. Arrows step 2% (10% with Shift). */

export interface SplitPaneProps {
  left: React.ReactNode
  right: React.ReactNode
  defaultFraction?: number
  minFraction?: number
  maxFraction?: number
  storageKey?: string
  ariaLabel?: string
  className?: string
}

function loadFraction(key: string | undefined, fallback: number): number {
  if (!key || typeof window === "undefined") return fallback
  const raw = window.localStorage.getItem(`splitpane:${key}`)
  if (!raw) return fallback
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 && n < 1 ? n : fallback
}

export function SplitPane({
  left,
  right,
  defaultFraction = 0.55,
  minFraction = 0.28,
  maxFraction = 0.75,
  storageKey,
  ariaLabel = "Resize panes",
  className,
}: SplitPaneProps): JSX.Element {
  const [fraction, setFraction] = React.useState(() =>
    loadFraction(storageKey, defaultFraction),
  )
  const containerRef = React.useRef<HTMLDivElement>(null)
  const draggingRef = React.useRef(false)

  const persist = React.useCallback(
    (value: number) => {
      if (!storageKey || typeof window === "undefined") return
      window.localStorage.setItem(`splitpane:${storageKey}`, String(value))
    },
    [storageKey],
  )

  const clamp = React.useCallback(
    (v: number) => Math.max(minFraction, Math.min(maxFraction, v)),
    [minFraction, maxFraction],
  )

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    draggingRef.current = true
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    const el = containerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const next = (e.clientX - rect.left) / rect.width
    setFraction(clamp(next))
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    persist(fraction)
  }

  const stepAndPersist = (next: number) => {
    const clamped = clamp(next)
    setFraction(clamped)
    persist(clamped)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 0.1 : 0.02
    if (e.key === "ArrowLeft") {
      e.preventDefault()
      stepAndPersist(fraction - step)
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      stepAndPersist(fraction + step)
    } else if (e.key === "Home") {
      e.preventDefault()
      stepAndPersist(minFraction)
    } else if (e.key === "End") {
      e.preventDefault()
      stepAndPersist(maxFraction)
    } else if (e.key === "Enter") {
      e.preventDefault()
      stepAndPersist(defaultFraction)
    }
  }

  return (
    <div
      ref={containerRef}
      className={cn("flex h-full min-h-0 w-full", className)}
    >
      <div className="min-h-0 min-w-0" style={{ flex: `${fraction} 1 0%` }}>
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={ariaLabel}
        aria-valuenow={Math.round(fraction * 100)}
        aria-valuemin={Math.round(minFraction * 100)}
        aria-valuemax={Math.round(maxFraction * 100)}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={onKeyDown}
        className="group relative flex w-1.5 shrink-0 cursor-col-resize items-center justify-center bg-border-soft hover:bg-brand/50 focus-visible:bg-brand focus-visible:outline-none"
      >
        <span
          className="h-8 w-0.5 rounded-full bg-border transition-colors duration-120 ease-out group-hover:bg-white group-focus-visible:bg-white"
          aria-hidden="true"
        />
      </div>
      <div className="min-h-0 min-w-0" style={{ flex: `${1 - fraction} 1 0%` }}>
        {right}
      </div>
    </div>
  )
}
